import { Box } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { type JSX, useMemo, useState } from 'react';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { saveInherentRiskQuestionnaire } from 'Api/UcrmTprm/UcrmTprmApi';
import { Alert } from 'Components/Alert/Alert';
import { Button } from 'Components/Buttons/Buttons';
import { FileManagementAreaWithSiblings, FileManagementAreaWithSiblingsHookValues, fileManagementAreaWithSiblingsHasFiles, useFileManagementAreas } from 'Components/Files/FileManagementArea';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { QuestionDate } from 'Components/Questions/QuestionDate';
import { QuestionFileUpload } from 'Components/Questions/QuestionFileUpload';
import { QuestionFreeformText } from 'Components/Questions/QuestionFreeformText';
import { QuestionMultipleSelect } from 'Components/Questions/QuestionMultipleSelect';
import { QuestionSingleSelect } from 'Components/Questions/QuestionSingleSelect';
import { RequiresAttentionMarker } from 'Components/RequiresAttentionMarker/RequiresAttentionMarker';
import { Text } from 'Components/Text/Text';
import { UnsavedChangesWarning } from 'Components/Text/UnsavedChangesWarning';
import { FormFieldTooltip } from 'Components/Tooltips/FormFieldTooltip';
import { ConfirmSubmitIrqModal, ConfirmSubmitIrqModalProps } from 'Components/UcrmTprm/ConfirmSubmitIrqModal/ConfirmSubmitIrqModal';
import { SubmitIrqSelectionsValue } from 'Components/UcrmTprm/SubmitIrqSelections/SubmitIrqSelections';
import { ICON_SAVE, ICON_SUBMIT } from 'Config/Icons';
import { TPRM_IRQ_DISABLED_BECAUSE_AWAITING_ASSESSMENT, TPRM_IRQ_DISABLED_BECAUSE_PERFORMING_DUE_DILIGENCE, UCRM_IRQ_DISABLED_BECAUSE_AWAITING_ASSESSMENT, UCRM_IRQ_DISABLED_BECAUSE_PERFORMING_DUE_DILIGENCE } from 'Config/Tooltips';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { useUnsavedChangesTracking } from 'Hooks/Forms';
import { AiContentAdvisorFeature } from 'Models/ArtificialIntelligence';
import { AssessmentSubjectArchiveBase, AssessmentSubjectForNonPrivilegedUserBase } from 'Models/AssessmentSubject';
import { DueDiligenceAssessmentConfiguration } from 'Models/DueDiligence';
import { AssessmentWorkflowState } from 'Models/DueDiligenceWorkflow';
import { UploadedFile } from 'Models/Files';
import { InherentRiskQuestionnaireResponse, InherentRiskQuestionnaireSection, IrqQuestion, IrqQuestionType, RiskRatingPointThresholds, irqQuestionIsAnswered } from 'Models/InherentRisk';
import { FinalizationState } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { RiskRating, RiskRatingSelectOptions, riskRatingAsString } from 'Models/RiskRating';
import { ThirdPartyService, TprmSaveOrSubmitInherentRiskQuestionnaireRequest } from 'Models/TPRM';
import { UcrmSaveOrSubmitInherentRiskQuestionnaireRequest, UseCase } from 'Models/Ucrm';

import styles from './InherentRiskQuestionnaireForm.module.css';

enum Modals {
    ClearIRQModal,
    ConfirmSubmitIrqModal,
    None,
}

const getUniqueIdForSection = (sectionIndex: number) => `section${sectionIndex}`;
const getUniqueIdForQuestion = (sectionIndex: number, questionIndex: number) => `section${sectionIndex}question${questionIndex}`;

/**
 * Whether an IRQ question has been answered, accounting for files that are staged for upload but have not been saved yet.
 * `irqQuestionIsAnswered` can't determine this on its own: until the IRQ is saved, a File Upload question's answer lives in the file management hook rather than on the question itself.
 */
const irqQuestionIsAnsweredIncludingStagedFiles = (question: IrqQuestion, sectionIndex: number, questionIndex: number, fileManagementHookValues: FileManagementAreaWithSiblingsHookValues): boolean => {
    return question._type === IrqQuestionType.FILE_UPLOAD ? fileManagementAreaWithSiblingsHasFiles(fileManagementHookValues, getUniqueIdForQuestion(sectionIndex, questionIndex)) : irqQuestionIsAnswered(question);
};

export type InherentRiskQuestionnaireUserTypeAndData =
    | {
          historical: false;
          userHasFullAccess: true;
          assessmentConfigurations: DueDiligenceAssessmentConfiguration[];
          assessmentSubject: ThirdPartyService | UseCase;
          defaultIrq: InherentRiskQuestionnaireResponse;
      }
    | {
          historical: false;
          userHasFullAccess: false;
          assessmentSubject: AssessmentSubjectForNonPrivilegedUserBase;
          defaultIrq: InherentRiskQuestionnaireResponse;
      }
    | {
          historical: true;
          userHasFullAccess: true;
          assessmentSubject: AssessmentSubjectArchiveBase;
          defaultIrq: InherentRiskQuestionnaireResponse;
      };

export interface InherentRiskQuestionnaireFormProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    documentApi: DocumentApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
    onFormSubmitBegin: () => void;
    onFormSubmitFailed: (errorMessage: string) => void;
    onFormSubmitSucceeded: (updatedIrq: InherentRiskQuestionnaireResponse) => Promise<void>;
    userTypeAndData: InherentRiskQuestionnaireUserTypeAndData;

    highlightMissingInformation?: boolean;
}

/**
 * A reusable component that renders an IRQ's sections and questions, and their corresponding form fields. Also renders a Save button and, if the corresponding assessment subject is not a draft, a Submit button.
 * Used on the IRQ page (current and historical) as well as on the TPRM and UCRM Intake pages, which is why props are used to notify the parent when there is a result for the save/submit request.
 *
 * TODO: Consider testing the display of files for File Upload questions, as well as the calculation of whether those questions have been answered. There was at one time bugs for both (particularly for sections with a mix of File Upload and non-File-Upload questions). Writing regression tests for those bugs may or may not make sense.
 */
export const InherentRiskQuestionnaireForm = (props: InherentRiskQuestionnaireFormProps) => {
    const [displayedModal, setDisplayedModal] = useState<Modals>();
    const [requestState, setRequestState] = useState<'idle' | 'saving' | 'submitting'>('idle');
    const [questionnaire, setQuestionnaire] = useState<InherentRiskQuestionnaireResponse>(props.userTypeAndData.defaultIrq);

    // At certain times, we want unanswered questions and/or an undefined risk rating to draw the user's attention with red text.
    // But this looks too urgent/aggressive on initial page load. Highlight missing information only after the user has tried to submit the IRQ without completing it, or the parent component wants to force the highlighting (which also occurs when the user has tried to submit, via `TprmIntake` or `UcrmIntake`).
    const [userHasTriedToSubmitWithInsufficientInfo, setUserHasTriedToSubmitWithInsufficientInfo] = useState(false);
    const showMissingInformationMarkers = props.highlightMissingInformation === true || userHasTriedToSubmitWithInsufficientInfo;

    const [submitRequestWithManagedFiles, fileManagementHookValues, hasUnsavedFileChanges] = useFileManagementAreas<InherentRiskQuestionnaireResponse>(
        props.documentApi,
        (() => {
            const filesMap = new Map<string, UploadedFile[]>();

            props.userTypeAndData.defaultIrq.sections.forEach((section, sectionIndex) => {
                filesMap.set(getUniqueIdForSection(sectionIndex), section.files);

                section.questions.forEach((question, questionIndex) => {
                    if (question._type === IrqQuestionType.FILE_UPLOAD) {
                        filesMap.set(getUniqueIdForQuestion(sectionIndex, questionIndex), question.answer ? question.answer : []);
                    }
                });
            });

            return filesMap;
        })()
    );

    const [hasUnsavedChanges, onFormFieldValuesSaved] = useUnsavedChangesTracking(questionnaire, hasUnsavedFileChanges);

    const overallCommentsAi = useAiContentAdvisor(props.artificialIntelligenceApi, questionnaire.comments ?? '', props.module === ProductModule.UCRM ? AiContentAdvisorFeature.UCRM_IRQ_OVERALL_COMMENTS : AiContentAdvisorFeature.TPRM_IRQ_OVERALL_COMMENTS, props.onFormSubmitFailed);

    const recommendedRiskRating = useMemo(() => {
        return questionnaire ? calculateRecommendedInherentRiskScore(questionnaire.sections, questionnaire.thresholds) : undefined;
    }, [questionnaire]);

    /** Whether each question has been answered. (The type is an array of arrays because each section holds an array of questions.) */
    const questionsAreAnswered: boolean[][] = useMemo(() => {
        return questionnaire.sections.map((section, sectionIndex) => section.questions.map((question, questionIndex) => irqQuestionIsAnsweredIncludingStagedFiles(question, sectionIndex, questionIndex, fileManagementHookValues)));
    }, [fileManagementHookValues, questionnaire.sections]);

    const sectionProgressStrings: string[] = useMemo(() => {
        return questionnaire.sections.map((section, sectionIndex) => {
            if (!section.is_applicable) {
                return `Completed (not relevant to this ${props.module === ProductModule.UCRM ? 'use case' : 'service'})`;
            } else {
                const numberOfQuestions = section.questions.length;
                const numberOfQuestionsAnswered = questionsAreAnswered[sectionIndex].filter(Boolean).length;
                return numberOfQuestionsAnswered === numberOfQuestions ? 'Completed' : `${Math.round((numberOfQuestionsAnswered / numberOfQuestions) * 100)}% completed`;
            }
        });
    }, [props.module, questionnaire.sections, questionsAreAnswered]);

    const handleSubmitClicked = () => {
        const errorMessage = _getDataIsInsufficientForSubmittingIrqReason(questionnaire, questionsAreAnswered);
        if (errorMessage) {
            props.onFormSubmitFailed(errorMessage);
            setUserHasTriedToSubmitWithInsufficientInfo(true);
        } else {
            setDisplayedModal(Modals.ConfirmSubmitIrqModal);
        }
    };

    /**
     * Set the inherent risk rating for the assessment subject.
     * @param submit Indicates whether the IRQ should be saved as in-progress or submitted as complete.
     * @param submitIrqSelections Indicates which questionnaires/assessments should be generated for the assessment subject, and (TPRM only) to which third-party contacts a registration link should be sent.
     */
    const saveQuestionnaire = async (submit: boolean, submitIrqSelections: SubmitIrqSelectionsValue): Promise<void> => {
        props.onFormSubmitBegin();
        setRequestState(submit ? 'submitting' : 'saving');

        try {
            const updatedIrq = await submitRequestWithManagedFiles(async (getFileUpdatesByFileManagementAreaId) => {
                const request: TprmSaveOrSubmitInherentRiskQuestionnaireRequest & UcrmSaveOrSubmitInherentRiskQuestionnaireRequest = {
                    submit: submit,
                    comments: questionnaire.comments,
                    inherent_risk_score: questionnaire.inherent_risk_score,
                    section_selections: questionnaire.sections.map((section, sectionIndex) => {
                        // Note: if a section does not apply, the backend will remove all answers, files, and text for that section. For simplicity, the frontend still sends everything.
                        return {
                            is_applicable: section.is_applicable,
                            answers: section.questions.map((question, questionIndex) => {
                                return (() => {
                                    switch (question._type) {
                                        case IrqQuestionType.DATE:
                                            return { _type: IrqQuestionType.DATE, value: question.answer };
                                        case IrqQuestionType.FREEFORM_TEXT:
                                            return { _type: IrqQuestionType.FREEFORM_TEXT, value: question.answer };
                                        case IrqQuestionType.MULTIPLE_SELECT:
                                            return { _type: IrqQuestionType.MULTIPLE_SELECT, value: question.answer };
                                        case IrqQuestionType.SINGLE_SELECT:
                                            return { _type: IrqQuestionType.SINGLE_SELECT, value: question.answer };
                                        case IrqQuestionType.FILE_UPLOAD:
                                            return { _type: IrqQuestionType.FILE_UPLOAD, value: getFileUpdatesByFileManagementAreaId(getUniqueIdForQuestion(sectionIndex, questionIndex)) };
                                    }
                                })();
                            }),
                            text: section.text,
                            file_updates: getFileUpdatesByFileManagementAreaId(getUniqueIdForSection(sectionIndex)),
                        };
                    }),
                    submit_selections: submitIrqSelections,
                };

                return (await saveInherentRiskQuestionnaire(props.moduleApi, props.userTypeAndData.assessmentSubject.vendor_id, props.userTypeAndData.assessmentSubject.id, request)).data;
            });

            setQuestionnaire(updatedIrq);
            await props.onFormSubmitSucceeded(updatedIrq);
            onFormFieldValuesSaved(updatedIrq);
        } catch (error) {
            console.log(error);
            props.onFormSubmitFailed(error.message);
        } finally {
            setRequestState('idle');
        }
    };

    const handleChangeOverallComments = (event: React.FormEvent<HTMLInputElement>): void => {
        setQuestionnaire({ ...questionnaire, comments: event.currentTarget.value });
    };

    const handleChangeSelectedRiskRating = (value: ChangeEventType): void => {
        setQuestionnaire({ ...questionnaire, inherent_risk_score: value as InherentRiskQuestionnaireResponse['inherent_risk_score'] });
    };

    const onConfirmBeginRiskWorkflow = (submitIrqSelections: SubmitIrqSelectionsValue) => {
        setDisplayedModal(Modals.None);
        saveQuestionnaire(true, submitIrqSelections);
    };

    const getConfirmSubmitIrqModalProps = (assessmentSubject: ThirdPartyService | UseCase, assessmentConfigurations: DueDiligenceAssessmentConfiguration[]): ConfirmSubmitIrqModalProps => ({
        assessmentConfigurations,
        assessmentSubject,
        hideModal: () => setDisplayedModal(Modals.None),
        inherentRiskRating: questionnaire.inherent_risk_score!,
        module: props.module,
        onConfirm: onConfirmBeginRiskWorkflow,
    });

    const assessmentSubjectIsADraft = props.userTypeAndData.assessmentSubject.finalization_state !== FinalizationState.FINALIZED;

    // Why null, rather than undefined? It's a safeguard against new due diligence workflow states being added in the future and not being handled here (as using `undefined` would allow the function not to return anything).
    const alertForDisabledIrq: JSX.Element | null = (() => {
        if (props.userTypeAndData.historical) {
            return <Alert type="warning">This inherent risk questionnaire is from a historical due diligence workflow and cannot be edited.</Alert>;
        }

        switch (props.userTypeAndData.assessmentSubject.assessment_workflow_data.state) {
            case AssessmentWorkflowState.EVALUATING_INHERENT_RISK:
                // The IRQ can, of course, be edited when inherent risk is being evaluated.
                // Only TPRM users (or admins) can do this, but no special checks are done here, as the backend will reject requests to load the IRQ (or assessment subject details) from users without full access once the assessment subject has been finalized.
                return null;
            case AssessmentWorkflowState.AWAITING_ASSESSMENT:
                if (!assessmentSubjectIsADraft) {
                    // The IRQ can be edited before the due diligence workflow has been started only if the assessment subject is still a draft.
                    return (
                        <Alert type="warning">
                            <div className={styles.disabledIrqAlert}>
                                The inherent risk questionnaire has been submitted and cannot be edited at this time.
                                <FormFieldTooltip text={props.module === ProductModule.UCRM ? UCRM_IRQ_DISABLED_BECAUSE_AWAITING_ASSESSMENT : TPRM_IRQ_DISABLED_BECAUSE_AWAITING_ASSESSMENT} />
                            </div>
                        </Alert>
                    );
                } else {
                    return null;
                }
            case AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE:
                // The IRQ can never be edited while due diligence is being performed.
                return (
                    <Alert type="warning">
                        <div className={styles.disabledIrqAlert}>
                            The inherent risk questionnaire has been submitted and cannot be edited at this time.
                            <FormFieldTooltip text={props.module === ProductModule.UCRM ? UCRM_IRQ_DISABLED_BECAUSE_PERFORMING_DUE_DILIGENCE : TPRM_IRQ_DISABLED_BECAUSE_PERFORMING_DUE_DILIGENCE} />
                        </div>
                    </Alert>
                );
        }
    })();

    /** The main point of this variable not being a boolean is that when the IRQ is not editable (because of the assessment subject's due diligence workflow state, not because of an in-progress request), inapplicable sections should not be collapsed. */
    const formDisabledReason = (() => {
        if (requestState !== 'idle') {
            return 'submittingRequest';
        } else if (alertForDisabledIrq !== null) {
            return 'irqNotEditable';
        } else {
            return 'none';
        }
    })();

    return (
        <>
            {displayedModal === Modals.ConfirmSubmitIrqModal && props.userTypeAndData.userHasFullAccess && !props.userTypeAndData.historical && <ConfirmSubmitIrqModal {...getConfirmSubmitIrqModalProps(props.userTypeAndData.assessmentSubject, props.userTypeAndData.assessmentConfigurations)} />}
            <Box component="form" noValidate>
                <div>
                    {/* TODO: Ideally, the placement of this alert would be consistent with the placement of the alerts on the pages for historical assessments and historical final reviews, or vice-versa. */}
                    {alertForDisabledIrq}
                    {questionnaire.sections.map((section, sectionIndex) => {
                        return (
                            <SectionDisplay
                                key={section.name}
                                artificialIntelligenceApi={props.artificialIntelligenceApi}
                                assessmentState={props.userTypeAndData.assessmentSubject.assessment_workflow_data.state}
                                fileManagementHookValues={fileManagementHookValues}
                                module={props.module}
                                onAiError={props.onFormSubmitFailed}
                                disabledReason={formDisabledReason}
                                documentApi={props.documentApi}
                                questionsAreAnswered={questionsAreAnswered[sectionIndex]}
                                section={section}
                                sectionIndex={sectionIndex}
                                sectionProgressString={sectionProgressStrings[sectionIndex]}
                                showUnansweredQuestionMarkers={showMissingInformationMarkers}
                                onUpdateSection={(updatedSection) => {
                                    const newSections = [...questionnaire.sections];
                                    newSections[sectionIndex] = updatedSection;
                                    setQuestionnaire({ ...questionnaire, sections: newSections });
                                }}
                            />
                        );
                    })}
                </div>
                <fieldset disabled={formDisabledReason !== 'none'}>
                    <div>
                        <Text variant="Header2" noStyles>
                            Overall
                        </Text>
                        <hr />
                    </div>

                    <Text noStyles variant="Text4" toUppercase>
                        Sections Progress
                    </Text>
                    <div className={styles.sectionsProgress}>
                        <div>
                            {questionnaire.sections.map((section, index) => (
                                <div key={index}>
                                    <Text noStyles>{section.name}</Text>
                                </div>
                            ))}
                        </div>
                        <div>
                            {sectionProgressStrings.map((text, index) => (
                                <div key={index}>
                                    <Text noStyles>{text}</Text>
                                </div>
                            ))}
                        </div>
                    </div>

                    {showMissingInformationMarkers && questionnaire.inherent_risk_score === undefined && (
                        <RequiresAttentionMarker type="negative" size="sm">
                            Required rating not selected
                        </RequiresAttentionMarker>
                    )}
                    <FormFieldSelect formFieldId="selectedRiskRating" formFieldLabel="Inherent Risk Rating" options={RiskRatingSelectOptions} selectedOption={questionnaire.inherent_risk_score} handleChange={handleChangeSelectedRiskRating} required={!assessmentSubjectIsADraft} badge={recommendedRiskRating ? `Recommendation: ${riskRatingAsString(recommendedRiskRating)}` : undefined} disabled={formDisabledReason !== 'none'} />
                    <FormFieldTextAreaAi formFieldId="overallComments" formFieldLabel="Comments" handleChange={handleChangeOverallComments} value={questionnaire.comments ?? ''} disabled={formDisabledReason !== 'none'} aiContentAdvisorValues={overallCommentsAi} />

                    {alertForDisabledIrq === null && (
                        <div className={styles.buttonsContainer}>
                            <Button fontAwesomeImage={ICON_SAVE} variant={assessmentSubjectIsADraft ? 'primary' : 'secondary'} isLoading={requestState === 'saving'} loadingText="Saving..." onClick={() => saveQuestionnaire(false, { assessment_selections: [], third_party_contacts: [] })}>
                                Save
                            </Button>
                            {!assessmentSubjectIsADraft && (
                                <Button fontAwesomeImage={ICON_SUBMIT} variant="primary" isLoading={requestState === 'submitting'} loadingText="Submitting..." onClick={handleSubmitClicked}>
                                    Submit
                                </Button>
                            )}
                        </div>
                    )}
                    <UnsavedChangesWarning hasUnsavedChanges={hasUnsavedChanges} />
                </fieldset>
            </Box>
        </>
    );
};

interface SectionDisplayProps {
    /** When the IRQ is in a state where it can be edited, the content of sections marked irrelevant are hidden. But if the IRQ can't be edited, the entire form is disabled: in that case, sections are expanded so that users can see which questions were disregarded when the IRQ was submitted. */
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    assessmentState: AssessmentWorkflowState;
    disabledReason: 'none' | 'submittingRequest' | 'irqNotEditable';
    documentApi: DocumentApi;
    fileManagementHookValues: FileManagementAreaWithSiblingsHookValues;
    module: DueDiligenceModule;
    onAiError: (message: string) => void;
    onUpdateSection: (updatedSection: InherentRiskQuestionnaireSection) => void;
    /** Whether each question in the section has been answered, in the same order as `section.questions`. */
    questionsAreAnswered: boolean[];
    section: InherentRiskQuestionnaireSection;
    sectionIndex: number;
    sectionProgressString: string;
    showUnansweredQuestionMarkers: boolean;
}

const SectionDisplay = (props: SectionDisplayProps) => {
    const sectionCommentsAi = useAiContentAdvisor(props.artificialIntelligenceApi, props.section.text ?? '', props.module === ProductModule.UCRM ? AiContentAdvisorFeature.UCRM_IRQ_SECTION_COMMENTS : AiContentAdvisorFeature.TPRM_IRQ_SECTION_COMMENTS, props.onAiError);

    return (
        <div className={styles.section}>
            <div>
                <div className={styles.sectionNameAndCheckboxContainer}>
                    <div className={styles.sectionNameContainer}>
                        <Text variant="Header3" noStyles>
                            {props.section.name}
                        </Text>
                        <Text noStyles variant="Text4">
                            {props.sectionProgressString}
                        </Text>
                    </div>
                    <div className={styles.checkboxContainer}>
                        <Checkbox
                            disabled={props.disabledReason !== 'none'}
                            checked={props.section.is_applicable}
                            color="default"
                            onChange={(event: React.FormEvent<HTMLInputElement>) => {
                                props.onUpdateSection({ ...props.section, is_applicable: event.currentTarget.checked });
                            }}
                        />
                        <Text noStyles>{props.module === ProductModule.UCRM ? 'Relevant to this use case' : 'Relevant to this service'}</Text>
                        <FormFieldTooltip text={props.module === ProductModule.UCRM ? 'Indicates whether the questions in this section are applicable to the use case. If unchecked, responses in this section will not be required for this use case.' : 'Indicates whether the questions in this section are applicable to the third-party service. If unchecked, responses in this section will not be required for this service.'} />
                    </div>
                </div>
            </div>
            {(props.section.is_applicable || props.disabledReason === 'irqNotEditable') && (
                <>
                    {props.section.questions.map((question, questionIndex) => {
                        const questionDisplayProps: QuestionDisplayProps = {
                            documentApi: props.documentApi,
                            fileManagementHookValues: props.fileManagementHookValues,
                            question,
                            questionIsAnswered: props.questionsAreAnswered[questionIndex],
                            section: props.section,
                            sectionIndex: props.sectionIndex,
                            questionIndex,
                            onUpdateQuestion: (updatedQuestion) => {
                                props.onUpdateSection({ ...props.section, questions: props.section.questions.map((q, i) => (i === questionIndex ? updatedQuestion : q)) });
                            },
                            disabled: props.disabledReason !== 'none',
                            showUnansweredQuestionMarkers: props.showUnansweredQuestionMarkers,
                        };
                        return (
                            <div className={styles.question} key={question.text}>
                                <QuestionDisplay {...questionDisplayProps} />
                            </div>
                        );
                    })}
                    <Text variant="Text2">Additional Information</Text>
                    <FormFieldTextAreaAi
                        formFieldId={`${props.section.name} comments`}
                        formFieldLabel="Comments"
                        handleChange={(event: React.FormEvent<HTMLInputElement>) => {
                            props.onUpdateSection({ ...props.section, text: event.currentTarget.value });
                        }}
                        value={props.section.text}
                        disabled={props.disabledReason !== 'none'}
                        aiContentAdvisorValues={sectionCommentsAi}
                    />
                    <FileManagementAreaWithSiblings uniqueId={getUniqueIdForSection(props.sectionIndex)} disabled={props.disabledReason !== 'none'} documentApi={props.documentApi} fileManagementHookValues={props.fileManagementHookValues} />
                </>
            )}
        </div>
    );
};

interface QuestionDisplayProps {
    documentApi: DocumentApi;
    fileManagementHookValues: FileManagementAreaWithSiblingsHookValues;
    question: IrqQuestion;
    questionIsAnswered: boolean;
    section: InherentRiskQuestionnaireSection;
    sectionIndex: number;
    questionIndex: number;
    onUpdateQuestion: (updatedQuestion: IrqQuestion) => void;
    disabled: boolean;
    showUnansweredQuestionMarkers: boolean;
}

/** Note the narrowed `const` in each case: TypeScript does not preserve the narrowing of `props.question` inside the `onAnswerChanged` closures, but it does preserve a `const`'s. */
const QuestionDisplay = (props: QuestionDisplayProps): JSX.Element => {
    const formFieldId = `section${props.sectionIndex}Question${props.questionIndex}AnswerLabel`;
    const showUnansweredQuestionMarker = props.showUnansweredQuestionMarkers && props.section.is_applicable && !props.questionIsAnswered;

    switch (props.question._type) {
        case IrqQuestionType.DATE: {
            const question = props.question;

            return (
                <QuestionDate
                    // Commenting to force multiple lines.
                    formFieldId={formFieldId}
                    onAnswerChanged={(date) => props.onUpdateQuestion({ ...question, answer: tryToConvertDateToString(date) })}
                    question={question}
                    questionNumber={props.questionIndex + 1}
                    showUnansweredQuestionMarker={showUnansweredQuestionMarker}
                    disabled={props.disabled}
                />
            );
        }
        case IrqQuestionType.FILE_UPLOAD: {
            return (
                <QuestionFileUpload
                    // Commenting to force multiple lines.
                    documentApi={props.documentApi}
                    fileManagementHookValues={props.fileManagementHookValues}
                    question={props.question}
                    questionNumber={props.questionIndex + 1}
                    showUnansweredQuestionMarker={showUnansweredQuestionMarker}
                    uniqueIdForFileManagementArea={getUniqueIdForQuestion(props.sectionIndex, props.questionIndex)}
                    disabled={props.disabled}
                />
            );
        }
        case IrqQuestionType.FREEFORM_TEXT: {
            const question = props.question;

            return (
                <QuestionFreeformText
                    // Commenting to force multiple lines.
                    formFieldId={formFieldId}
                    onAnswerChanged={(text) => props.onUpdateQuestion({ ...question, answer: text })}
                    question={question}
                    questionNumber={props.questionIndex + 1}
                    showUnansweredQuestionMarker={showUnansweredQuestionMarker}
                    disabled={props.disabled}
                />
            );
        }
        case IrqQuestionType.MULTIPLE_SELECT: {
            const question = props.question;

            return (
                <QuestionMultipleSelect
                    // Commenting to force multiple lines.
                    formFieldId={formFieldId}
                    onAnswerChanged={(selectedIndices) => props.onUpdateQuestion({ ...question, answer: selectedIndices })}
                    question={question}
                    questionNumber={props.questionIndex + 1}
                    showUnansweredQuestionMarker={showUnansweredQuestionMarker}
                    disabled={props.disabled}
                />
            );
        }
        case IrqQuestionType.SINGLE_SELECT: {
            const question = props.question;

            return (
                <QuestionSingleSelect
                    // Commenting to force multiple lines.
                    formFieldLabelId={formFieldId}
                    onAnswerChanged={(selectedIndex) => props.onUpdateQuestion({ ...question, answer: selectedIndex })}
                    question={question}
                    questionNumber={props.questionIndex + 1}
                    showUnansweredQuestionMarker={showUnansweredQuestionMarker}
                    disabled={props.disabled}
                />
            );
        }
    }
};

const calculateRecommendedInherentRiskScore = (sections: InherentRiskQuestionnaireSection[], thresholds: RiskRatingPointThresholds): RiskRating | undefined => {
    let totalPoints = 0;
    for (const section of sections) {
        if (!section.is_applicable) {
            continue;
        }

        for (const question of section.questions) {
            if (!(question._type === IrqQuestionType.SINGLE_SELECT || question._type === IrqQuestionType.MULTIPLE_SELECT)) {
                continue;
            } else if (!irqQuestionIsAnswered(question)) {
                // If any points-based question is unanswered, no recommendation is given.
                return undefined;
            } else if (question._type === IrqQuestionType.SINGLE_SELECT) {
                totalPoints += question.options[question.answer!].points;
            } else if (question._type === IrqQuestionType.MULTIPLE_SELECT) {
                for (const answerIndex of question.answer) {
                    totalPoints += question.options[answerIndex].points;
                }
            }
        }
    }

    if (totalPoints <= thresholds.low) {
        return RiskRating.LOW;
    } else if (totalPoints <= thresholds.low_moderate) {
        return RiskRating.LOW_MODERATE;
    } else if (totalPoints <= thresholds.moderate) {
        return RiskRating.MODERATE;
    } else if (totalPoints <= thresholds.moderate_high) {
        return RiskRating.MODERATE_HIGH;
    } else {
        return RiskRating.HIGH;
    }
};

/** Necessary because of unreliable typing on `FormFieldDatePicker`'s part. It may provide `null` as the user edits the text in the picker. Extracted into a function here to call attention to the problem: we should probably holistically improve usages of `FormFieldDatePicker`. */
const tryToConvertDateToString = (maybeDate: Date | null): string | undefined => {
    if (maybeDate) {
        return jsDateToIso8601(maybeDate);
    } else {
        return undefined;
    }
};

// TODO: The use of these two functions is hacky/confusing. Evaluate refactoring options after evaluating the strength/weakness of the checkboxes UX of the Review step of the TPRM Intake page as a team--that new page is the driver for this differing logic.
/** Either returns `undefined`, if the user has provided enough data to submit the IRQ, or a string with a reason for why the IRQ can't be submitted. Takes files waiting to be uploaded into account. */
const _getDataIsInsufficientForSubmittingIrqReason = (irq: InherentRiskQuestionnaireResponse, questionsAreAnswered: boolean[][]): string | undefined => {
    if (irq.inherent_risk_score === undefined || !irq.sections.every((section, sectionIndex) => !section.is_applicable || questionsAreAnswered[sectionIndex].every(Boolean))) {
        return 'Before the inherent risk questionnaire can be submitted, all questions must be answered and an inherent risk rating must be selected.';
    }
    return undefined;
};

/** Similar to the above function, but does not take files waiting to be uploaded into account. Used by the TPRM Intake component to control `highlightMissingInformation` based on whether the IRQ saved in the database can be submitted. */
export const getDataIsInsufficientForSubmittingIrqReason = (irq: InherentRiskQuestionnaireResponse): string | undefined => {
    if (irq.inherent_risk_score === undefined || !irq.sections.every((section) => !section.is_applicable || section.questions.every(irqQuestionIsAnswered))) {
        return 'Before the inherent risk questionnaire can be submitted, it must be saved with all questions answered and an inherent risk rating selected.';
    }
    return undefined;
};
